/**
 * Hook para buscar estatísticas de execução dos nodes Teste A/B
 * Retorna contagem e porcentagem de cada variante por node
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';

/**
 * Estatísticas de uma variante específica
 */
export interface VariantStats {
  count: number;
  percentage: number;
}

/**
 * Estatísticas de um node A/B Test
 */
export interface NodeStats {
  variants: Record<string, VariantStats>;
  total: number;
}

/**
 * Mapa de estatísticas por nodeId
 */
export interface ABTestStats {
  [nodeId: string]: NodeStats;
}

/**
 * Hook que busca e retorna estatísticas de execução dos nodes Teste A/B
 * @param flowId - ID do fluxo para buscar estatísticas
 */
export function useABTestStats(flowId: string | undefined) {
  const [stats, setStats] = useState<ABTestStats>({});
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Busca estatísticas do banco de dados
   */
  const fetchStats = useCallback(async () => {
    if (!flowId) return;

    setIsLoading(true);
    try {
      // Busca executions steps do tipo ab_test para este flow
      const { data, error } = await supabase
        .from('flow_execution_steps')
        .select(`
          node_id,
          output_data,
          flow_executions!inner(flow_id)
        `)
        .eq('node_type', 'ab_test')
        .eq('status', 'completed')
        .eq('flow_executions.flow_id', flowId);

      if (error) {
        logger.error('[useABTestStats] Erro ao buscar estatísticas:', error.message);
        return;
      }

      // Agrupa por node_id e variante
      const statsMap: ABTestStats = {};
      
      data?.forEach((step: any) => {
        const nodeId = step.node_id;
        const variant = step.output_data?.selected_variant;
        
        if (!variant) return;
        
        // Inicializa estrutura do node se não existir
        if (!statsMap[nodeId]) {
          statsMap[nodeId] = { variants: {}, total: 0 };
        }
        
        // Inicializa variante se não existir
        if (!statsMap[nodeId].variants[variant]) {
          statsMap[nodeId].variants[variant] = { count: 0, percentage: 0 };
        }
        
        // Incrementa contadores
        statsMap[nodeId].variants[variant].count++;
        statsMap[nodeId].total++;
      });

      // Calcula porcentagens
      Object.keys(statsMap).forEach((nodeId) => {
        const node = statsMap[nodeId];
        const total = node.total;
        
        Object.keys(node.variants).forEach((variantKey) => {
          const variantStats = node.variants[variantKey];
          variantStats.percentage = total > 0 
            ? Math.round((variantStats.count / total) * 100) 
            : 0;
        });
      });

      logger.debug('[useABTestStats] Estatísticas carregadas:', JSON.stringify(statsMap));
      setStats(statsMap);
    } catch (err) {
      logger.error('[useABTestStats] Erro inesperado:', String(err));
    } finally {
      setIsLoading(false);
    }
  }, [flowId]);

  // Busca inicial
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return { stats, isLoading, refetch: fetchStats };
}
